import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insidePages } from '../data/content';
import { CtaButton } from './CtaButton';
export function InsideLook() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const page = insidePages[index];
  const go = (next: number) => {
    setDirection(next > index || index === insidePages.length - 1 && next === 0 ? 1 : -1);
    setIndex((next + insidePages.length) % insidePages.length);
  };
  return <section id="preview" className="w-full bg-ink px-5 py-20 sm:py-28" data-id="element-120" data-mp="2h8c9el0hkm1x">
      <div className="mx-auto max-w-5xl" data-id="element-121" data-mp="2h5zt0cadmjqz">
        <div className="text-center" data-id="element-122" data-mp="2m6nlz3i61anu">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand" data-id="element-123" data-mp="2m5m3wf2qfnwm">
            Look inside the pack
          </span>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-[1.9rem] font-black leading-[1.08] tracking-tight text-cream sm:text-[2.6rem]" data-id="element-124" data-mp="3en7zikzyudwg">
            This is the material you&apos;ll be teaching from{' '}
            <span className="italic text-brand" data-id="element-125" data-mp="1xutvu2mr7t4x">on Monday.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-cream/55" data-id="element-126" data-mp="2bloygx6nte02">
            Real pages from the volumes — swipe through and see the level of
            detail before you decide.
          </p>
        </div>

        <div className="mt-12 grid items-center gap-10 md:grid-cols-[1.05fr_1fr] md:gap-14" data-id="element-127" data-mp="2lj3ckq541qp7">
          <div className="relative mx-auto w-full max-w-sm" data-id="element-128" data-mp="2gll22vbg33f3">
            <div className="relative aspect-[9/16] w-full overflow-hidden rounded-2xl bg-cream/5 ring-1 ring-cream/15" data-id="element-129" data-mp="2gn4hykt6vcgc">
              <AnimatePresence initial={false} mode="popLayout" custom={direction} data-id="element-130" data-mp="2plpyybj0ijf0">
                <motion.img key={page.image} src={page.image} alt={`Sample page: ${page.title}`} custom={direction} initial={{
                opacity: 0,
                x: direction * 28
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: direction * -28
              }} transition={{
                duration: 0.28,
                ease: [0.23, 1, 0.32, 1]
              }} className="absolute inset-0 h-full w-full object-cover object-top" data-id="element-131" data-mp="106jsnkb8ezc8" />
              </AnimatePresence>
            </div>

            <button type="button" onClick={() => go(index - 1)} aria-label="Previous page" className="absolute -left-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-cream text-ink shadow-soft transition-[transform,background-color] duration-150 ease-out hover:bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-brand/40 sm:-left-5" data-id="element-132" data-mp="397d7b56w7b0e">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-133" data-mp="uz35leuouh2" />
            </button>
            <button type="button" onClick={() => go(index + 1)} aria-label="Next page" className="absolute -right-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-cream text-ink shadow-soft transition-[transform,background-color] duration-150 ease-out hover:bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-brand/40 sm:-right-5" data-id="element-134" data-mp="5tsh1ey1unhr">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-135" data-mp="2911c9ket6w2i" />
            </button>
          </div>

          <div className="text-center md:text-left" data-id="element-136" data-mp="24z601kvcay9y">
            <AnimatePresence mode="wait" data-id="element-137" data-mp="250hce92upeco">
              <motion.div key={page.title} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-138" data-mp="208fua6j04teq">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand" data-id="element-139" data-mp="2nytp24sz16d0">
                  {page.volume}
                </span>
                <h3 className="mt-3 font-display text-2xl font-black leading-tight text-cream sm:text-3xl" data-id="element-140" data-mp="27hhjx8jslkh5">
                  {page.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-cream/60" data-id="element-141" data-mp="1yiw2jgq24bhy">
                  {page.text}
                </p>
              </motion.div>
            </AnimatePresence>

            <div className="mt-8 flex items-center justify-center gap-4 md:justify-start" data-id="element-142" data-mp="2r709e5kd6um8">
              <span className="font-display text-sm font-bold tabular-nums text-cream/70" data-id="element-143" data-mp="2r9e7knaccmy0">
                {String(index + 1).padStart(2, '0')}
                <span className="text-cream/30" data-id="element-144" data-mp="2hg5qnb6bud5j"> / {String(insidePages.length).padStart(2, '0')}</span>
              </span>
              <ul className="flex items-center gap-2" data-id="element-145" data-mp="2meqmkyejqao6">
                {insidePages.map((item, dotIndex) => <li key={item.title} data-id="element-146" data-mp="3a3t4m7d3e5j7" data-mpi={dotIndex}>
                    <button type="button" onClick={() => go(dotIndex)} aria-label={`Show ${item.volume}`} aria-current={dotIndex === index} className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 ${dotIndex === index ? 'w-7 bg-brand' : 'w-3 bg-cream/25 hover:bg-cream/45'}`} data-id="element-147" data-mp="3ex4g4lsunahg" />
                  </li>)}
              </ul>
            </div>

            <div className="mt-9" data-id="element-148" data-mp="3fagf29x3ewns">
              <CtaButton href="#offer" label="I want these pages" data-id="element-149" data-mp="3fbrfq0v8roao" />
              <p className="mt-4 text-[11px] font-medium text-cream/40" data-id="element-150" data-mp="3abwqs2v118s9">
                +52 sessions like these · Instant download · Lifetime access
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}